import type { ReactNode, RefObject } from 'react';
import { CountryPhoneInput } from '@/components/auth/CountryPhoneInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ContributePageHeading } from '@/components/contribute/ContributePageHeading';
import type { CountryData } from '@/lib/countries';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { buildAdminContractHref, buildAdminContractPreview } from './admin-contract-template';
import type { BecomeAdminCopy, BecomeAdminFormValues, BecomeAdminStep } from './types';

type BecomeAdminFormController = {
  activeStep: number;
  canProceed: boolean;
  completion: number;
  contractTemplateHtml: string | null;
  fileInputRef: RefObject<HTMLInputElement>;
  isSubmitting: boolean;
  processingPayment: boolean;
  showPaymentModal: boolean;
  values: BecomeAdminFormValues;
  goNext: () => Promise<void>;
  goPrev: () => void;
  onFiles: (fileList: FileList | null) => void;
  processCardPayment: () => Promise<void>;
  removeFile: (index: number) => void;
  setField: <Key extends keyof BecomeAdminFormValues>(key: Key, value: BecomeAdminFormValues[Key]) => void;
  setShowPaymentModal: (open: boolean) => void;
};

type BecomeAdminViewProps = {
  form: BecomeAdminFormController;
  steps: BecomeAdminStep[];
  t: BecomeAdminCopy;
};

type FieldProps = {
  label: string;
  optionalLabel?: string;
  optional?: boolean;
  children: ReactNode;
};

const onlyDigits = (value: string) => value.replace(/\D/g, '');

const formatCardNumber = (value: string) =>
  onlyDigits(value)
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();

const formatCardExpiry = (value: string) => {
  const digits = onlyDigits(value).slice(0, 4);
  if (!digits) return '';

  if (digits.length === 1) {
    return Number(digits) > 1 ? `0${digits}/` : digits;
  }

  const month = Math.min(Math.max(Number(digits.slice(0, 2)), 1), 12).toString().padStart(2, '0');
  return digits.length <= 2 ? month : `${month}/${digits.slice(2)}`;
};

const formatCardCvc = (value: string) => onlyDigits(value).slice(0, 4);

const isCardPaymentReady = (values: BecomeAdminFormValues) => {
  const cardNumberDigits = onlyDigits(values.cardNumber);
  const cardExpiryDigits = onlyDigits(values.cardExpiry);
  const cardCvcDigits = onlyDigits(values.cardCVC);
  const month = Number(cardExpiryDigits.slice(0, 2));

  return (
    cardNumberDigits.length >= 12 &&
    cardNumberDigits.length <= 19 &&
    cardExpiryDigits.length === 4 &&
    month >= 1 &&
    month <= 12 &&
    (cardCvcDigits.length === 3 || cardCvcDigits.length === 4)
  );
};
const inputClassName =
  'h-12 rounded-2xl border-border/45 bg-slate-900/[0.025] dark:bg-white/[0.025] px-4 text-sm shadow-none outline-none transition-colors placeholder:text-muted-foreground/38 hover:border-border/65 focus-visible:border-brand/30 focus-visible:ring-0 focus-visible:ring-offset-0';


function Field({ label, optional, optionalLabel, children }: FieldProps) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/60">
        {label}
        {optional ? (
          <span className="text-[10px] font-medium normal-case tracking-normal text-muted-foreground/40">
            {optionalLabel}
          </span>
        ) : null}
      </Label>
      {children}
    </div>
  );
}

function StepRail({ activeStep, completion, steps, t }: Pick<BecomeAdminViewProps, 'steps' | 't'> & { activeStep: number; completion: number }) {
  return (
    <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
      <div className="space-y-3 pt-5">
        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/55">
          <span>{t.becomeAdminProgress}</span>
          <span className="text-brand">{completion}%</span>
        </div>
        <div className="h-px overflow-hidden bg-border/45">
          <div className="h-full bg-gradient-to-r from-brand/70 via-primary/70 to-success/70 transition-[width] duration-500 ease-out" style={{ width: `${completion}%` }} />
        </div>
      </div>

      <nav className="space-y-4">
        {steps.map((step, index) => {
          const active = index === activeStep;
          const done = index < activeStep;

          return (
            <div key={step.id} className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-3">
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors',
                    active
                      ? 'border-brand/40 bg-brand/[0.08] text-brand'
                      : done
                        ? 'border-success/25 bg-success/[0.06] text-success'
                        : 'border-border/50 text-muted-foreground/55',
                  )}
                >
                  {done ? <Icon name="check" className="h-3.5 w-3.5" /> : index + 1}
                </span>
                {index < steps.length - 1 ? <span className="mt-3 h-10 w-px bg-border/35" /> : null}
              </div>

              <div className="pb-2">
                <p className={cn('text-sm font-semibold tracking-[-0.02em]', active ? 'text-foreground' : 'text-muted-foreground/75')}>
                  {step.title}
                </p>
                <p className="mt-1 max-w-xs text-xs leading-5 text-muted-foreground/55">
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </nav>

      <p className="border-t border-border/35 pt-5 text-xs leading-6 text-muted-foreground/60">
        {t.becomeAdminSecureReview}
      </p>
    </aside>
  );
}

function CompanyStep({ form, t }: Pick<BecomeAdminViewProps, 'form' | 't'>) {
  const { setField, values } = form;

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field label={t.becomeAdminCompanyName}>
        <Input className={inputClassName} value={values.companyName} onChange={(event) => setField('companyName', event.target.value)} />
      </Field>
      <Field label={t.becomeAdminRegistrationNumber}>
        <Input className={inputClassName} value={values.registrationNumber} onChange={(event) => setField('registrationNumber', event.target.value)} />
      </Field>
      <Field label={t.becomeAdminSector}>
        <Input className={inputClassName} placeholder={t.becomeAdminSectorPlaceholder} value={values.sector} onChange={(event) => setField('sector', event.target.value)} />
      </Field>
      <Field label={t.becomeAdminCompanyPhone}>
        <CountryPhoneInput
          country={values.companyCountry}
          phone={values.companyPhone}
          onCountryChange={(country: CountryData) => setField('companyCountry', country.name)}
          onPhoneChange={(phone) => setField('companyPhone', phone)}
          countryPlaceholder={t.country}
          phonePlaceholder={t.phoneNumberPlaceholder}
        />
      </Field>
      <Field label={t.becomeAdminCompanyEmail}>
        <Input className={inputClassName} type="email" value={values.companyEmail} onChange={(event) => setField('companyEmail', event.target.value)} />
      </Field>
      <Field label={t.becomeAdminCompanyWebsite} optional optionalLabel={t.becomeAdminOptional}>
        <Input className={inputClassName} value={values.companyWebsite} onChange={(event) => setField('companyWebsite', event.target.value)} />
      </Field>
      <Field label={t.becomeAdminCompanyAddress}>
        <Input className={cn(inputClassName, 'sm:col-span-2')} value={values.companyAddress} onChange={(event) => setField('companyAddress', event.target.value)} />
      </Field>
    </div>
  );
}

function RepresentativeStep({ form, t }: Pick<BecomeAdminViewProps, 'form' | 't'>) {
  const { setField, values } = form;

  return (
    <div className="space-y-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={t.becomeAdminRepName}>
          <Input className={inputClassName} value={values.repName} onChange={(event) => setField('repName', event.target.value)} />
        </Field>
        <Field label={t.becomeAdminRepRole} optional optionalLabel={t.becomeAdminOptional}>
          <Input className={inputClassName} value={values.repRole} onChange={(event) => setField('repRole', event.target.value)} />
        </Field>
        <Field label={t.becomeAdminRepEmail}>
          <Input className={inputClassName} type="email" value={values.repEmail} onChange={(event) => setField('repEmail', event.target.value)} />
        </Field>
        <Field label={t.becomeAdminRepPhone} optional optionalLabel={t.becomeAdminOptional}>
          <CountryPhoneInput
            country={values.repCountry}
            phone={values.repPhone}
            onCountryChange={(country: CountryData) => setField('repCountry', country.name)}
            onPhoneChange={(phone) => setField('repPhone', phone)}
            countryPlaceholder={t.country}
            phonePlaceholder={t.phoneNumberPlaceholder}
          />
        </Field>
      </div>

      <section className="pt-6">
        <div className="mb-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-brand">
          {t.becomeAdminAccountSection}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label={t.becomeAdminUsername}>
            <Input className={inputClassName} value={values.username} onChange={(event) => setField('username', event.target.value)} />
          </Field>
          <Field label={t.becomeAdminPassword}>
            <Input className={inputClassName} type="password" value={values.password} disabled={values.useSSO} onChange={(event) => setField('password', event.target.value)} />
          </Field>
        </div>

        <label className="mt-5 flex cursor-pointer items-center gap-3 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <input
            type="checkbox"
            checked={values.useSSO}
            onChange={(event) => setField('useSSO', event.target.checked)}
            className="h-4 w-4 rounded border-border bg-transparent accent-brand"
          />
          <span>{t.becomeAdminUseSso}</span>
        </label>
      </section>
    </div>
  );
}

function FileRows({ form, t }: Pick<BecomeAdminViewProps, 'form' | 't'>) {
  if (form.values.files.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-border/35 pt-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/55">
        {t.becomeAdminFilesAttached}
      </p>
      {form.values.files.map((file, index) => (
        <div key={`${file.name}-${file.size}-${index}`} className="flex items-center justify-between gap-3 rounded-2xl border border-border/35 bg-slate-900/[0.018] dark:bg-white/[0.018] px-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground/88">{file.name}</p>
            <p className="text-xs text-muted-foreground/45">{Math.round(file.size / 1024)} {t.becomeAdminFileSizeUnit}</p>
          </div>
          <button type="button" onClick={() => form.removeFile(index)} className="text-xs font-semibold text-danger transition-colors hover:text-danger">
            {t.becomeAdminRemoveFile}
          </button>
        </div>
      ))}
    </div>
  );
}

function DocumentsStep({ form, t }: Pick<BecomeAdminViewProps, 'form' | 't'>) {
  const { fileInputRef, onFiles, setField, setShowPaymentModal, values } = form;
  const contractPreview = buildAdminContractPreview(values, t);
  const contractHref = buildAdminContractHref(values, t, form.contractTemplateHtml);

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-foreground">{t.becomeAdminDocumentsTitle}</h3>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground/65">{t.becomeAdminDocumentsDesc}</p>
        </div>

        <input ref={fileInputRef} className="hidden" type="file" multiple onChange={(event) => onFiles(event.target.files)} />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-border/45 bg-slate-900/[0.018] dark:bg-white/[0.018] px-4 text-sm font-semibold text-foreground/85 transition-colors hover:border-brand/25 hover:text-foreground"
        >
          <Icon name="upload" className="h-4 w-4 text-brand" />
          {t.becomeAdminUploadDocuments}
        </button>

        <FileRows form={form} t={t} />
      </section>

      <section className="space-y-5 pt-6">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-foreground">{t.becomeAdminContractTitle}</h3>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground/65">{t.becomeAdminContractInstruction}</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <a href={contractHref} download="umbaji-admin-contract.html" className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-border/45 px-4 text-sm font-semibold text-foreground/85 transition-colors hover:border-brand/25 hover:text-foreground">
            <Icon name="download" className="h-4 w-4 text-brand" />
            {t.becomeAdminDownloadContract}
          </a>

          <label className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-full border border-success/20 bg-success/[0.045] px-4 text-sm font-semibold text-success transition-colors hover:border-success/35">
            <Icon name="fileText" className="h-4 w-4" />
            {t.becomeAdminUploadSignedContract}
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(event) => setField('signedContract', event.target.files?.[0] ?? null)} />
          </label>
        </div>

        <div className="py-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/55">
            {t.becomeAdminContractPreview}
          </p>
          {contractPreview.length > 0 ? (
            <dl className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
              {contractPreview.map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/42">{item.label}</dt>
                  <dd className="mt-1 truncate text-sm text-foreground/82">{item.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm leading-6 text-muted-foreground/58">{t.becomeAdminContractPreviewEmpty}</p>
          )}
        </div>

        {values.signedContract ? (
          <p className="text-sm text-muted-foreground/65">
            {t.becomeAdminSignedContractSelected} <span className="text-foreground/88">{values.signedContract.name}</span>
          </p>
        ) : null}

        <div className="space-y-3 pt-5">
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-6 text-muted-foreground transition-colors hover:text-foreground">
            <input type="checkbox" checked={values.accepted} onChange={(event) => setField('accepted', event.target.checked)} className="mt-1 h-4 w-4 rounded border-border bg-transparent accent-brand" />
            <span>{t.becomeAdminTermsConsent}</span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-6 text-muted-foreground transition-colors hover:text-foreground">
            <input type="checkbox" checked={values.rgpdAccepted} onChange={(event) => setField('rgpdAccepted', event.target.checked)} className="mt-1 h-4 w-4 rounded border-border bg-transparent accent-brand" />
            <span>{t.becomeAdminRgpdConsent}</span>
          </label>
        </div>

        <button type="button" onClick={() => setShowPaymentModal(true)} className="inline-flex h-10 items-center gap-2 rounded-full border border-brand/25 bg-brand/[0.055] px-4 text-sm font-semibold text-brand transition-colors hover:border-brand/45 hover:bg-brand/[0.08]">
          <Icon name="wallet" className="h-4 w-4" />
          {t.becomeAdminPayVisa}
        </button>
      </section>
    </div>
  );
}

function PaymentModal({ form, t }: Pick<BecomeAdminViewProps, 'form' | 't'>) {
  if (!form.showPaymentModal) return null;

  const cardPaymentReady = isCardPaymentReady(form.values);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-xl">
      <div className="w-full max-w-md border-t border-brand/20 bg-background/95 p-6 shadow-[0_-1px_0_rgba(255,255,255,0.04)]">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand">{t.payment}</p>
            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{t.becomeAdminPaymentTitle}</h3>
          </div>
          <button type="button" onClick={() => form.setShowPaymentModal(false)} className="rounded-full border border-border/45 p-2 text-muted-foreground transition-colors hover:text-foreground">
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label={t.becomeAdminCardNumber}>
            <Input className={inputClassName} inputMode="numeric" autoComplete="cc-number" placeholder="1234 5678 9012 3456" value={form.values.cardNumber} onChange={(event) => form.setField('cardNumber', formatCardNumber(event.target.value))} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.becomeAdminCardExpiry}>
              <Input className={inputClassName} inputMode="numeric" autoComplete="cc-exp" placeholder="MM/YY" value={form.values.cardExpiry} onChange={(event) => form.setField('cardExpiry', formatCardExpiry(event.target.value))} />
            </Field>
            <Field label={t.becomeAdminCardCvc}>
              <Input className={inputClassName} inputMode="numeric" autoComplete="cc-csc" placeholder="CVC" value={form.values.cardCVC} onChange={(event) => form.setField('cardCVC', formatCardCvc(event.target.value))} />
            </Field>
          </div>
        </div>

        <div className="mt-7 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" size="sm" className="h-10 rounded-full bg-transparent shadow-none" onClick={() => form.setShowPaymentModal(false)}>
            {t.becomeAdminCancel}
          </Button>
          <Button size="sm" className="h-10 rounded-full px-5 shadow-none disabled:opacity-35" onClick={form.processCardPayment} disabled={form.processingPayment || !cardPaymentReady}>
            {form.processingPayment ? t.becomeAdminProcessing : t.becomeAdminPayVisa}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ActiveStep({ form, steps, t }: BecomeAdminViewProps) {
  const step = steps[form.activeStep];

  return (
    <section className="pt-7">
      <div className="mb-7 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand">{step.title}</p>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground/65">{step.description}</p>
        </div>
        <span className="text-sm text-muted-foreground/55">{form.activeStep + 1}/{steps.length}</span>
      </div>

      {step.id === 'company' ? <CompanyStep form={form} t={t} /> : null}
      {step.id === 'representative' ? <RepresentativeStep form={form} t={t} /> : null}
      {step.id === 'documents' ? <DocumentsStep form={form} t={t} /> : null}

      <div className="mt-8 flex flex-col gap-3 pt-5 sm:flex-row sm:justify-between">
        <Button variant="outline" size="sm" onClick={form.goPrev} disabled={form.activeStep === 0 || form.isSubmitting} className="h-10 rounded-full bg-transparent px-4 shadow-none disabled:opacity-30">
          <Icon name="arrowLeft" className="h-4 w-4" />
          {t.becomeAdminPrevious}
        </Button>
        <Button size="sm" onClick={form.goNext} disabled={form.isSubmitting || !form.canProceed} className="h-10 rounded-full px-5 shadow-none disabled:opacity-35">
          {form.activeStep < steps.length - 1 ? t.becomeAdminNext : form.isSubmitting ? t.becomeAdminProcessing : t.becomeAdminSubmit}
          <Icon name="arrowRight" className="h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}

export function BecomeAdminView({ form, steps, t }: BecomeAdminViewProps) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:py-12">
        <div className="grid gap-10 lg:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)]">
          <StepRail activeStep={form.activeStep} completion={form.completion} steps={steps} t={t} />

          <main className="min-w-0 space-y-9">
            <ContributePageHeading eyebrow={t.becomeAdminEyebrow} title={t.becomeAdminTitle} description={t.becomeAdminSubtitle} titleClassName="text-3xl sm:text-4xl" />
            <ActiveStep form={form} steps={steps} t={t} />
          </main>
        </div>
      </div>
      <PaymentModal form={form} t={t} />
    </div>
  );
}




